import { Link, useNavigate } from "react-router-dom";
import { Upload, FileVideo, Palette } from "lucide-react";
import { StatusBanner } from "@/components/common/StatusBanner";
import { swingLabTokens } from "@/styles/swing-lab.tokens";
import { useRecentVideos } from "../data/useRecentVideos";
import { VideoList } from "../components/VideoList";
import { useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { getAnalysisIdForVideo } from "../data/runs";

export function SwingLabUploadPage() {
  const navigate = useNavigate();
  const { data: videos, isLoading, error } = useRecentVideos(10);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { toast } = useToast();

  // Archived: useMuxUpload moved to archive/mux
  // const { state, upload, cancel } = useMuxUpload({...});
  // Mock state object matching the expected interface
  const state = { 
    phase: 'idle' as 'idle' | 'uploading' | 'processing' | 'error' | 'done',
    progress: 0,
    pct: 0 // Alias for progress (some code uses pct)
  };
  const upload = (file: File) => { toast({ title: 'Mux upload archived', description: 'Feature moved to archive/mux' }); };
  const cancel = () => {};

  const handlePlayVideo = async (videoId: string) => {
    // Try to resolve videoId to analysisId (runId)
    // videoId might be video_asset_id or video table ID
    try {
      const analysisId = await getAnalysisIdForVideo(videoId);
      if (analysisId) {
        // Navigate to authoritative Analysis page
        navigate(`/analysis/${analysisId}`);
      } else {
        // No analysis exists yet - redirect to progress page or show message
        toast.info('Analysis in progress', {
          description: 'This video is being analyzed. Please check back soon.'
        });
        // Optionally navigate to progress page if we have a runId
        // For now, just show toast
      }
    } catch (error) {
      console.error('[UploadPage] Error resolving analysis:', error);
      // Fallback: try navigating to swing-lab/analysis which will redirect
      navigate(`/swing-lab/analysis?id=${videoId}`);
    }
  };

  const onPickFile = () => {
    setUploadError(null);
    fileRef.current?.click();
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const file = e.target.files?.[0];
    if (file) {
      upload(file);
    }
    // Reset input to allow selecting the same file again
    e.currentTarget.value = '';
  };

  const busy = state.phase !== 'idle' && state.phase !== 'error' && state.phase !== 'done';

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-4xl mx-auto px-5 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-slate-900 mb-2">
            Swing Lab – Upload & Test
          </h1>
          <p className="text-slate-600">
            Upload and analyze your golf swing videos with AI-powered insights.
          </p>
        </div>

        <StatusBanner
          type="info"
          title="Development Phase"
          description="This is a scaffold for Swing Lab v1. Cloudflare Stream integration working; ML analysis comes in Step 2."
          className="mb-8"
        />

        {/* Upload Error */}
        {uploadError && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm mb-6">
            {uploadError}
          </div>
        )}

        {/* Upload State */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-12 text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Upload className="h-8 w-8 text-white" />
          </div>
          
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            Upload Your Swing Video
          </h3>
          <p className="text-slate-600 mb-8 max-w-md mx-auto">
            MP4/MOV, ≤20s, ≤4K, ≤250MB. Ready to analyze your golf swing?
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={onPickFile}
                disabled={busy}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Upload className="h-4 w-4" />
                Choose Video
              </button>

              {busy && state.phase === 'uploading' && (
                <div className="text-sm text-slate-600">
                  Uploading… {Math.round(state.progress)}%
                </div>
              )}
              
              {busy && state.phase !== 'uploading' && (
                <div className="text-sm text-slate-600 capitalize">
                  {state.phase}…
                </div>
              )}

              {busy && (
                <button
                  type="button"
                  onClick={cancel}
                  className="text-sm text-slate-600 underline hover:text-slate-800"
                >
                  Cancel
                </button>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/design/aura-preview"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Palette className="h-4 w-4" />
                View Aura Preview
              </Link>
              <Link
                to="/swing-lab/design-mock"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <FileVideo className="h-4 w-4" />
                Design Mock
              </Link>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileRef}
            type="file"
            accept="video/mp4,video/quicktime"
            className="sr-only"
            onChange={onFileChange}
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>

        {/* Recent Videos */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Recent Videos</h2>
            <p className="text-sm text-slate-600 mt-1">
              Your most recent swing videos. Limits: ≤20s, 4K max.
            </p>
          </div>
          <div className="p-6">
            {error ? (
              <div className="text-center py-8">
                <div className="text-red-600 text-sm">
                  Failed to load videos: {error.message}
                </div>
              </div>
            ) : (
              <VideoList 
                videos={videos || []} 
                onPlay={handlePlayVideo}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}